import React, { useMemo } from 'react'
import { getDescendants, NodeModel } from '@minoru/react-dnd-treeview'
import NodeIcon from './NodeIcon'
import styles from './styles.module.css'
import { Box, HStack, IconButton, Text } from '@chakra-ui/react'
import { ReactComponent as IconFolderPlus } from 'shared/assets/bsFolderPlus.svg'
import { ReactComponent as IconTrash } from 'shared/assets/bsTrash.svg'
import SimpleInput from 'shared/components/SimpleInput'

const TREE_X_OFFSET = 22

const Node: React.FC<{
  node: NodeModel
  depth: number
  isOpen: boolean
  isDropTarget: boolean
  treeData: NodeModel[]
  onClick: (id: NodeModel['id']) => void
  getPipeHeight: (id: string | number, treeData: NodeModel[]) => number
  onDelete: (id: NodeModel['id']) => void
  createSubFolder: (id: NodeModel['id']) => void
  onChangeText: (id: NodeModel['id'], text: string) => void
}> = ({
  node,
  depth,
  isOpen,
  isDropTarget,
  onClick,
  treeData,
  getPipeHeight,
  onDelete,
  createSubFolder,
  onChangeText
}) => {
  const hasDescendants = useMemo(() => {
    const d = getDescendants(treeData, node.id)
    return d.length > 0
  }, [treeData, node])

  const indent = depth * TREE_X_OFFSET

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation()
    onClick(node.id)
  }

  const onDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onDelete(node.id)
  }

  const onCreateSubFolder = (e: React.MouseEvent) => {
    e.stopPropagation()
    createSubFolder(node.id)
  }

  const renderIcons = () => {
    if (node.droppable) {
      return (
        <HStack>
          <IconButton
            variant={'link'}
            aria-label='add sub folder'
            colorScheme='black'
            size='sm'
            icon={<IconFolderPlus width={16} />}
            onClick={onCreateSubFolder}
            display={'none'}
            _groupHover={{ display: 'flex' }}
          />
          <IconButton
            variant={'link'}
            aria-label='delete folder'
            colorScheme='red'
            size='sm'
            icon={<IconTrash width={12} />}
            isDisabled={hasDescendants}
            onClick={onDeleteClick}
            display={'none'}
            _groupHover={{ display: 'flex' }}
          />
          <div
            className={`${styles.expandIconWrapper} ${
              isOpen ? styles.isOpen : ''
            }`}
          >
            <svg
              width='16'
              height='16'
              viewBox='0 0 16 16'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M10.5866 5.99969L7.99997 8.58632L5.41332 5.99969C5.15332 5.73969 4.73332 5.73969 4.47332 5.99969C4.21332 6.25969 4.21332 6.67965 4.47332 6.93965L7.5333 9.99965C7.59497 10.0615 7.66823 10.1105 7.7489 10.144C7.82957 10.1775 7.91603 10.1947 8.0033 10.1947C8.09063 10.1947 8.1771 10.1775 8.25777 10.144C8.33837 10.1105 8.41163 10.0615 8.4733 9.99965L11.5333 6.93965C11.7933 6.67965 11.7933 6.25969 11.5333 5.99969C11.2733 5.74635 10.8466 5.73969 10.5866 5.99969Z'
                fill='black'
              />
            </svg>
          </div>
        </HStack>
      )
    }
  }

  const renderLabel = () => {
    if (node.droppable) {
      return (
        <SimpleInput
          value={node.text}
          onChange={(v: string) => onChangeText(node.id, v)}
          size='md'
          w='md'
          pl={2}
          variant='unstyled'
          hideIcon
          containerProps={{ w: 'fit-content' }}
        />
      )
    } else {
      return (
        <Text color='black.700' pl={2}>
          {node.text}
        </Text>
      )
    }
  }

  return (
    <div
      role='group'
      className={`${styles.nodeWrapper} tree-node ${
        node.droppable && isDropTarget ? styles.dropTarget : ''
      }`}
      style={{ marginInlineStart: indent }}
      onClick={handleToggle}
    >
      <Box color='sun.800'>
        <NodeIcon
          type={node.droppable ? (isOpen ? 'folder-open' : 'folder') : null}
        />
      </Box>
      <div
        className={styles.pipeX}
        style={{ width: depth > 0 ? TREE_X_OFFSET - 9 : 0 }}
      />
      {getDescendants(treeData, node.parent)[0].id === node.id && (
        <div
          className={styles.pipeY}
          style={{
            height: Math.max(0, getPipeHeight(node.parent, treeData) - 8)
          }}
        />
      )}

      {renderLabel()}
      {renderIcons()}
    </div>
  )
}

export default Node
